import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useLoader } from '../../../../../helpers/useLoader';
import { ImageScopeName } from '../../../../annotation/question.model';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { AnnotationDiscardConfirmModal } from '../components/AnnotationDiscardConfirmModal';
import { ToolDeleteModal } from '../components/ToolDeleteModal';
import { mapToolType, ToolType } from '../helpers/tools.helpers';
import { IProjectDetailsToolsBl } from '../projectDetailsTools.bl';
import { IQuestion } from '../projectDetailsTools.models';
import { QuestionModalContainer } from './QuestionModal.container';

interface IProps {
  questionId: string;
  isReadonly: boolean;
  isAttribute: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  showEditModal: boolean;
  modalHeader: string;
}

export const EditQuestionContainer = observer((props: IProps): ReactElement => {
  const projectDetailsToolsBl = useInjection(IProjectDetailsToolsBl.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const [editedQuestion, setEditedQuestion] = useState<IQuestion | undefined>(undefined);
  const [strategy, setStrategy] = useState<ToolDeleteStrategy>(ToolDeleteStrategy.None);
  const [showDiscardConfirmModal, setShowDiscardConfirmModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const question = projectDetailsToolsBl.store.questions.find((s) => s.id === props.questionId);
    setEditedQuestion(question);
  }, [props.questionId, projectDetailsToolsBl.store.questions]);

  const handleSubmit = async (question: IQuestion): Promise<void> => {
    const [deletedAnswers, deletedScopes] = getDeletedScopesAndAnswers(question);
    if (deletedAnswers.length || deletedScopes.length) {
      await withLoaderAsync(async () => {
        await projectDetailsToolsBl.getQuestionAnswersAndScopesUsageAsync(question.id, deletedAnswers, deletedScopes);
      });
      const isUsed = (projectDetailsToolsBl.store.toolsUsage?.imageCount ?? 0) > 0;
      if (isUsed) {
        setShowModal(true);
        setEditedQuestion(question);
        return;
      }
    }

    await withLoaderAsync(async () => {
      await projectDetailsToolsBl.updateQuestionAsync(question, strategy);
      props.onSubmit();
    }, 'submit-question-form-button');
  };

  const getDeletedScopesAndAnswers = (question: IQuestion): [string[], string[]] => {
    const existingQuestion = projectDetailsToolsBl.store.questions.find((s) => s.id === props.questionId);
    const existingAnswerIds = existingQuestion?.answers.map((x) => x.id) ?? [];
    const newAnswersIds = question.answers.map((x) => x.id);
    const deletedAnswers = existingAnswerIds.filter((x) => !newAnswersIds.includes(x));
    const deletedScopes = existingQuestion?.scopes.filter((x) => !question.scopes.includes(x)) ?? [];
    return [deletedAnswers, deletedScopes];
  };

  const handleUpdateConfirmed = async (): Promise<void> => {
    if (!editedQuestion) {
      return;
    }

    if (strategy === ToolDeleteStrategy.Discard) {
      const [deletedAnswers, deletedScopes] = getDeletedScopesAndAnswers(editedQuestion);
      await projectDetailsToolsBl.getQuestionAnswersAndScopesAffectAsync(editedQuestion.id, deletedAnswers, deletedScopes);
      setShowDiscardConfirmModal(true);
      return;
    }

    setShowModal(false);
    await withLoaderAsync(async () => {
      await projectDetailsToolsBl.updateQuestionAsync(editedQuestion, strategy);
      props.onSubmit();
    });
  };

  const handleUpdateWithDiscardConfirmed = async (): Promise<void> => {
    if (!editedQuestion) {
      return;
    }

    setShowDiscardConfirmModal(false);
    setShowModal(false);
    await withLoaderAsync(async () => {
      await projectDetailsToolsBl.updateQuestionAsync(editedQuestion, strategy);
      props.onSubmit();
    }, 'tool-delete-confirm-button');
  };

  const handleCancel = (): void => {
    if (!isLoading) {
      setShowModal(false);
    }
  };

  const handleStrategyChange = (strategy: ToolDeleteStrategy): void => {
    setStrategy(strategy);
  };

  const handleCancelDiscard = (): void => {
    if (!isLoading) {
      setShowDiscardConfirmModal(false);
    }
  };

  const questions = projectDetailsToolsBl.store.questions;
  const question = questions.find((s) => s.id === props.questionId);
  const toolUsage = projectDetailsToolsBl.store.toolsUsage;
  const toolAffect = projectDetailsToolsBl.store.toolAffect;

  return (
    <>
      <QuestionModalContainer
        question={question}
        isReadonly={props.isReadonly}
        questionsCount={questions.length}
        selectedQuestionTypes={questions.filter((q) => q.scopes.includes(ImageScopeName)).map((q) => q.type)}
        existingQuestionsAndAttributes={questions.map((q) => ({ text: q.questionText, type: q.type }))}
        onSubmit={handleSubmit}
        onCancel={props.onCancel}
        isAttribute={props.isAttribute}
        isLoading={isLoading}
        showModal={props.showEditModal}
        modalHeader={props.modalHeader}
      />
      <ToolDeleteModal
        strategy={strategy}
        showModal={showModal}
        onConfirm={handleUpdateConfirmed}
        onCancel={handleCancel}
        onStrategyChange={handleStrategyChange}
        toolType={mapToolType(props.isAttribute ? ToolType.Attribute : ToolType.Question)}
        annotationsCount={toolUsage?.imageCount || 0}
        objectsCount={toolUsage?.occurrenceCount || 0}
      />
      <AnnotationDiscardConfirmModal
        onCancel={handleCancelDiscard}
        onConfirm={handleUpdateWithDiscardConfirmed}
        showModal={showDiscardConfirmModal}
        annotationsCount={toolUsage?.imageCount || 0}
        objectsCount={toolAffect?.objectsCount || 0}
        questionsCount={toolAffect?.questionsCount || 0}
        attributesCount={toolAffect?.attributesCount || 0}
      />
    </>
  );
});
